import mapActions from "actions/map";
import modalActions from "actions/modal";
import cn from "classnames";
import PickerMap from "components/map/pickerMap";
import { MarkerCar, MarkerCluster, PlannedMarkerCar, RouteMarker } from "components/map/shared";
import Loader from "components/shared/loader";
import { bidPointTypesEnum } from "helpers/enums";
import { actualLineStyle, coordsToSegments, planDashLineStyle, planLineStyle } from "helpers/map";
import { enumLabel } from "helpers/system";
import { divIcon } from "leaflet";
import moment from "moment";
import React, { memo, useMemo, useRef, useState } from "react";
import { Marker, Polyline, Popup, Tooltip } from "react-leaflet";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { getShiftCarRouteForBid } from "sources/books/bids";
import { getCarLastBidInfo } from "sources/books/trucks";
import { DefaultTemplate } from "./templates";

const ShiftCarLocation = (props) => {
    const { carLastLocation, shiftCar, enums, shiftCarId } = props;
    const [loaded, setLoaded] = useState(false);
    const [data, setData] = useState({});
    const tooltipRef = useRef();

    if (!carLastLocation) {
        return null;
    }

    const eventHandlers = {
        popupopen() {
            tooltipRef.current?.setOpacity(0);
            if (!loaded) {
                getCarLastBidInfo(shiftCarId).then((data) => {
                    setData(data);
                    setLoaded(true);
                });
            }
        },
        popupclose() {
            tooltipRef.current?.setOpacity(0.9);
        },
    };
    const options = {
        position: carLastLocation.location,
        icon: divIcon({
            iconSize: [28, 82],
            className: "map__icon map-icon map-icon--blue-track map-icon--car map-icon--car--purple",
        }),
        eventHandlers,
    };
    const popupProps = {
        className: "map__popup map__popup--dark",
        autoPan: false,
        closeOnClick: false,
        closeButton: false,
    };
    const fixTime = moment(carLastLocation.fixedAt).format("DD.MM.YY H:mm");
    const { driver, trailer } = data;

    return (
        <Marker {...options}>
            <Popup {...popupProps}>
                <div
                    className={cn("map__popup-wrapper border-bottom-0 border-secondary", {
                        "loading loading--small": !loaded,
                    })}
                >
                    <div className="map-car__title">{shiftCar.number}</div>
                    {(driver || trailer) && (
                        <div className="map-car__data">
                            {!!driver && (
                                <div>
                                    <div className="d-inline-block">
                                        <strong className="pr-1">Водитель:</strong>
                                    </div>
                                    <div className="d-inline-block">
                                        {driver.shortName}
                                        {driver.phoneNumber && (
                                            <span className="map-car__secondary pl-1">({driver.phoneNumber})</span>
                                        )}
                                    </div>
                                </div>
                            )}
                            {!!trailer && (
                                <div>
                                    <strong className="pr-1">Прицеп:</strong>
                                    <span className="text-nowrap">
                                        {trailer.number}
                                        {trailer.typeId && (
                                            <span className="map-car__secondary pl-1">
                                                ({enumLabel(enums.trailerType, trailer.typeId)})
                                            </span>
                                        )}
                                    </span>
                                </div>
                            )}
                        </div>
                    )}
                    <div className="map-car__secondary font-italic">Обновлено в {fixTime}</div>
                </div>
            </Popup>
            <Tooltip permanent className="map__tooltip" direction="top" ref={tooltipRef}>
                <div>{shiftCar.number}</div>
            </Tooltip>
        </Marker>
    );
};

export default class MapPicker extends DefaultTemplate {
    componentDidMount() {
        if (this.props.shiftCarId) {
            getShiftCarRouteForBid(this.props.shiftCarId, this.props.bidId).then((shiftCarRoute) =>
                this.setState({ isLoading: false, ...this.initialState, shiftCarRoute }),
            );
        } else {
            this.setState({ isLoading: false, ...this.initialState });
        }
    }

    renderBody = () => {
        const callback = (data) => {
            this.props.callback(data);
            this.close();
        };
        if (this.state.isLoading) {
            return <Loader />;
        }
        const PickerContainer = memo(
            withRouter(
                connect(
                    mapStateToProps,
                    mapDispatchToProps,
                )((props) => {
                    const [viewport, setViewport] = useState(props.viewport);
                    const { route, bidLastCarLocation, enums } = this.props;
                    const { totalRoute, supportPoints, emptyMileageRoute } = useMemo(() =>
                        coordsToSegments(route?.segments || []),
                    );
                    const { carLastLocation, traveledRouteCoordinates } = useMemo(() => {
                        if (!this.state.shiftCarRoute) {
                            return {};
                        }
                        const { carLastLocation, traveledRouteCoordinates } = this.state.shiftCarRoute;
                        return {
                            carLastLocation: carLastLocation
                                ? {
                                      location: [...carLastLocation.location].reverse(),
                                      fixedAt: carLastLocation.fixedAt,
                                  }
                                : undefined,
                            traveledRouteCoordinates: traveledRouteCoordinates?.map((x) => [x[1], x[0]]),
                        };
                    }, [this.state.shiftCarRoute]);

                    const mergeMapViewport = (payload) => setViewport({ ...viewport, ...payload });

                    const renderRouteMarkers = (markers = []) => {
                        let index = -1;
                        const markersHtml = markers.map((item) => {
                            if (![bidPointTypesEnum.StartPoint, bidPointTypesEnum.ServicePoint].includes(item.type)) {
                                index++;
                            }
                            const props = { item, index };
                            return <RouteMarker key={item.id} {...props} />;
                        });
                        return <MarkerCluster maxClusterRadius={20}>{markersHtml}</MarkerCluster>;
                    };

                    const renderBidCarLocation = () => {
                        if (!bidLastCarLocation) {
                            return null;
                        }
                        const { estimatedCarLocation, estimatedAt } = bidLastCarLocation;
                        return (
                            <>
                                {bidLastCarLocation && <MarkerCar item={bidLastCarLocation} enums={enums} />}
                                {estimatedCarLocation && (
                                    <PlannedMarkerCar item={estimatedCarLocation} estimatedAt={estimatedAt} />
                                )}
                            </>
                        );
                    };

                    const renderSupportPoints = () => {
                        const props = {
                            icon: divIcon({ className: "route-icon route-icon--between", iconSize: [15, 15] }),
                        };
                        const pointRenderer = (point, index) => <Marker key={index} position={point} {...props} />;

                        return supportPoints.map(pointRenderer);
                    };

                    return (
                        <PickerMap
                            {...props}
                            viewport={viewport}
                            mergeMapViewport={mergeMapViewport}
                            data={{ callback, withoutRadius: true }}
                        >
                            {traveledRouteCoordinates && (
                                <Polyline
                                    positions={traveledRouteCoordinates}
                                    {...actualLineStyle.style}
                                    color={actualLineStyle.colors[0]}
                                />
                            )}
                            <Polyline positions={totalRoute} {...planLineStyle} />
                            <Polyline positions={emptyMileageRoute} {...planDashLineStyle} />
                            {renderRouteMarkers(this.props.bid.bidPoints)}
                            {renderBidCarLocation()}
                            <ShiftCarLocation
                                carLastLocation={carLastLocation}
                                shiftCar={this.props.shiftCar}
                                enums={this.props.enums}
                                shiftCarId={this.props.shiftCarId}
                            />
                            {renderSupportPoints()}
                        </PickerMap>
                    );
                }),
            ),
        );
        return <PickerContainer />;
    };
}

const mapStateToProps = (state) => ({
    mode: state.map.mode,
    viewport: state.map.viewport,
    data: state.map.data,
    fullscreen: state.layout.fullscreen,
    tileUrls: state.configs.tileUrls,
    params: state.map.params,
    organizationTimezone: state.profile.timezone,
    roles: state.profile.roles,
    enums: state.dictionaries.enums,
    profile: state.profile.profile,
    header: state.map.header,
});

const mapDispatchToProps = (dispatch) => ({
    switchMap: (params) => dispatch(mapActions.switchMap(params)),
    mergeMapData: (data) => dispatch(mapActions.mergeMapData(data)),
    changeCenter: (data) => dispatch(mapActions.changeCenter(data)),
    mergeParams: (data) => dispatch(mapActions.mergeParams(data)),
    modal: (params) => dispatch(modalActions.open(params)),
});
